import type { ReactNode } from 'react';
import { Section, ActionBar } from '@vnetwork/ui/layout';
import { Button, Skeleton } from '@vnetwork/ui/primitives';
import { StatusPill } from '@vnetwork/ui/data';
import { configTone, configLabel } from '../shared';

interface ConfigFormShellProps {
  title: string;
  description: string;
  isLoading?: boolean;
  isConfigured: boolean;
  dirty: boolean;
  busy: boolean;
  saving?: boolean;
  onSave: () => void;
  onRemove?: () => void;
  onCancel?: () => void;
  /** Override the status pill (e.g. show "Enabled"). */
  statusLabel?: string;
  saveLabel?: string;
  /** Optional banner rendered above the form (e.g. secondary-vendor drift). */
  drift?: ReactNode;
  children: ReactNode;
}

/**
 * Standard config-tab chrome: Section header + configured StatusPill + a
 * sticky Save/Remove/Cancel ActionBar. Drives the Security config forms.
 */
export function ConfigFormShell({
  title,
  description,
  isLoading,
  isConfigured,
  dirty,
  busy,
  saving,
  onSave,
  onRemove,
  onCancel,
  statusLabel,
  saveLabel,
  drift,
  children,
}: ConfigFormShellProps) {
  if (isLoading) {
    return (
      <Section title={title}>
        <Skeleton style={{ height: 260 }} />
      </Section>
    );
  }
  return (
    <Section
      title={title}
      description={description}
      actions={<StatusPill tone={configTone(isConfigured)}>{statusLabel ?? configLabel(isConfigured)}</StatusPill>}
    >
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        {drift}
        {children}
        <ActionBar
          start={
            isConfigured && onRemove ? (
              <Button variant="secondary" disabled={busy} onClick={onRemove}>
                Remove
              </Button>
            ) : undefined
          }
          end={
            <>
              {isConfigured && onCancel && (
                <Button variant="secondary" disabled={!dirty || busy} onClick={onCancel}>
                  Cancel
                </Button>
              )}
              <Button disabled={(isConfigured && !dirty) || busy} onClick={onSave}>
                {saving ? 'Saving…' : saveLabel ?? (isConfigured ? 'Save' : 'Create')}
              </Button>
            </>
          }
        />
      </div>
    </Section>
  );
}

/** Small helper for the common load→edit→dirty pattern in config tabs. */
export function jsonEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}
